<template>
  <v-layout column>
    <v-card-title>
      <div class="headline">{{ $t('network') }}</div>
      <v-btn
        @click="manualRefresh"
        color="primary"
        text
        icon
      >
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-icon v-if="hasItemChanges" v-on="on">sync_problem</v-icon>
            <v-icon v-else v-on="on">sync</v-icon>
          </template>
          <span v-if="hasItemChanges">{{ $t('refresh_warning_tip') }}</span>
          <span v-else>{{ $t('refresh_tip') }}</span>
        </v-tooltip>
      </v-btn>
      <v-btn class="ml-4" @click="openWizard">{{ $t('add_components') }}</v-btn>
      <v-spacer></v-spacer>
      <v-text-field
        append-icon="search"
        class="search"
        :label="$t('search')"
        single-line
        hide-details
        v-model="search"
      ></v-text-field>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :items="items"
      item-key="uid"
      :options="options"
      :search="search"
      sort-by="id"
    >
      <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
        <v-tooltip max-width="200" bottom :key="header.value">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              {{ header.text }}
            </span>
          </template>
          <span>
            {{ header.tooltip }}
          </span>
        </v-tooltip>
      </template>
      <template v-slot:item.label="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on" v-if="item.url">
              <a :href="item.url" class="ext-link" target="_blank">
                {{ item.label }}
                <v-icon size="12" color="primary">launch</v-icon>
              </a>
            </span>
            <span v-on="on" v-else>{{ item.label }}</span>
          </template>
          <span>{{ item.id }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.metadata.version="{ item }">
        <template v-if="item.metadata.version">
          {{ item.metadata.version }}
        </template>
        <template v-else>{{ $t('na') }}</template>
      </template>
      <template v-slot:item.health.status="{ item }">
        <span v-if="item.health.status === NodeStatus.ONLINE" class="accent--text">{{ $t('status.online') }}</span>
        <span v-else class="error--text">{{ $t('status.offline') }}</span>
      </template>
      <template v-slot:item.health.availabilityIssue="{ item }">
        <v-tooltip v-if="item.health.availabilityIssue" bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <template>
                <app-issue
                  :severity="item.health.availabilityIssue.severity"
                  :size="24"
                  :status="item.health.status"
                >
                </app-issue>
              </template>
            </span>
          </template>
          <span>
            <div>{{ item.health.availabilityIssue.description }}</div>
          </span>
        </v-tooltip>
        <template v-else><v-icon color="success">check_circle</v-icon></template>
      </template>
      <template v-slot:item.health.performanceIssue="{ item }">
        <v-tooltip v-if="item.health.performanceIssue" bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <template>
                <app-issue
                  :severity="item.health.performanceIssue.severity"
                  :size="24"
                  :status="item.health.status"
                >
                </app-issue>
              </template>
            </span>
          </template>
          <span>
            <div>{{ item.health.performanceIssue.description }}</div>
          </span>
        </v-tooltip>
        <template v-else><v-icon color="success">check_circle</v-icon></template>
      </template>
      <template v-slot:item.group="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">{{ $t(`groups.${item.group || 'other'}`) }}</span>
          </template>
          <div>
            <h4>{{ $t('columns.role') }}</h4>
            <app-node-attribute-list
              :array="item.roles"
              attr="role"
              :blacklist="['service']"
            ></app-node-attribute-list>
            <h4>{{ $t('columns.protocols') }}</h4>
            <app-node-attribute-list
              :array="item.roles"
              attr="protocol"
              :blacklist="[]"
            ></app-node-attribute-list>
          </div>
        </v-tooltip>
      </template>
      <template v-slot:item.metadata.externalIp="{ item }">
        <template v-if="item.metadata.externalIp">
          {{ item.metadata.externalIp }}
        </template>
        <template v-else>{{ $t('na') }}</template>
      </template>
      <template v-slot:item.metadata.provider="{ item }">
        <template v-if="item.metadata.provider">{{ item.metadata.provider }}</template>
        <app-lazy-value v-else-if="item.metadata.fetchProvider" :fetch="item.metadata.fetchProvider"></app-lazy-value>
        <template v-else>{{ $t('na') }}</template>
      </template>
      <template v-slot:item.timestamp="{ item }">
        {{ formatDistanceToNow(item.timestamp, { addSuffix: true }) }}
      </template>
    </v-data-table>
    <app-deploy :open="wizardIsOpen" :deployment="deployment" @close="closeWizard" />
    <v-snackbar
      v-model="snackbar"
      color="primary"
      :timeout="snackBarTimeout"
      bottom
      left
    >
      {{ snackBarMessage }}
      <v-btn color="accent" @click="snackbar = false" text>
        {{ snackBarClose }}
      </v-btn>
    </v-snackbar>
    <div class="pending" v-if="pending">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </v-layout>
</template>

<script>
import GeoLocationService from '@/services/geolocation'
import NetworkService from '@/services/network'
import DeployComponent from '@/components/Deploy.vue'
import IssueComponent from '@/components/Issue.vue'
import LazyValue from '@/components/LazyValue.vue'
import NodeAttributeListComponent from '@/components/NodeAttributeList.vue'
import { autoRefreshRate } from '@/constants'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import { NodeStatus } from '@/model/node'

const SnackBarType = {
  SUCCESS: 'success',
  INFO: 'info',
  ERROR: 'error'
}

export default {
  props: {
    deployment: String
  },
  data () {
    return {
      search: '',
      options: {
        itemsPerPage: 10
      },
      headers: [
        {
          align: 'left',
          text: this.$t('columns.name'),
          value: 'label',
          tooltip: this.$t('tips.name')
        },
        {
          align: 'left',
          text: this.$t('columns.version'),
          value: 'metadata.version',
          tooltip: this.$t('tips.version')
        },
        {
          align: 'left',
          text: this.$t('columns.namespace'),
          value: 'namespace',
          tooltip: this.$t('tips.namespace')
        },
        {
          align: 'left',
          text: this.$t('columns.deployment_status'),
          value: 'health.status',
          tooltip: this.$t('tips.deployment_status')
        },
        {
          align: 'center',
          text: this.$t('columns.availability'),
          value: 'health.availabilityIssue',
          tooltip: this.$t('tips.availability')
        },
        {
          align: 'center',
          text: this.$t('columns.performance'),
          value: 'health.performanceIssue',
          tooltip: this.$t('tips.performance')
        },
        {
          align: 'left',
          text: this.$t('columns.type'),
          value: 'group',
          tooltip: this.$t('tips.type')
        },
        {
          align: 'left',
          text: this.$t('columns.externalIp'),
          value: 'metadata.externalIp',
          tooltip: this.$t('tips.externalIp')
        },
        {
          align: 'left',
          text: this.$t('columns.provider'),
          value: 'metadata.provider',
          tooltip: this.$t('tips.provider')
        },
        {
          align: 'left',
          text: this.$t('columns.latest_report'),
          value: 'timestamp',
          tooltip: this.$t('tips.latest_report')
        }
      ],
      items: [],
      hasItemChanges: false,
      pending: false,
      snackbar: false,
      snackBarMessage: '',
      snackBarTimeout: 3000,
      snackBarClose: this.$t('snackbar.close'),
      NodeStatus,
      wizardIsOpen: false
    }
  },
  created () {
    this.reset()
  },
  mounted () {
    this.$root.$on('wizardOpen', () => {
      clearInterval(this.autoRefreshInterval)
    })
    this.$root.$on('wizardClose', () => {
      this.reset()
    })
  },
  destroyed () {
    clearInterval(this.autoRefreshInterval)
  },
  methods: {
    reset () {
      clearInterval(this.autoRefreshInterval)
      this.fetchNodes().then(() => {
        this.autoRefreshInterval = setInterval(this.autoRefresh, autoRefreshRate)
      })
    },
    fetchNodes () {
      this.pending = true
      return NetworkService.nodes(this.deployment).then((data) => {
        // Vuetify does not search recursively
        const fetchProvider = function () {
          if (this.provider) {
            return Promise.resolve(this.provider)
          } else {
            return GeoLocationService.getGeoLocationFromIp(this.externalIp)
              .catch(() => ({}))
              .then((ret) => {
                return this.provider = ret.provider
              })
          }
        }
        for (const item of data) {
          if (item.metadata && item.metadata.externalIp) {
            item.metadata.fetchProvider = fetchProvider.bind(item.metadata)
          }
        }
        this.items = data
      })
      .finally(() => this.pending = false)
    },
    autoRefresh () {
      NetworkService.nodes().then((data) => {
        // Map creation
        const nodeMap = {}
        data.forEach((item) => {
          nodeMap[item.id + '|' + item.namespace] = item
        })
        // Update nodes and verify if there is some removed.
        let itemsRemoved = false
        this.items = this.items.map((item) => {
          const key = item.id + '|' + item.namespace
          const node = nodeMap[key]
          if (node) {
            delete nodeMap[key]
            return node
          }
          itemsRemoved = true
          return item
        })
        // Verifiy if there is new nodes.
        const itemsAdded = Object.keys(nodeMap).length > 0
        // Validate or not if a notification needs to be shown.
        if (itemsRemoved || itemsAdded) {
          this.showSnackBar(SnackBarType.INFO, this.$t('snackbar.info'))
          this.hasItemChanges = true
        }
      })
    },
    manualRefresh () {
      this.pending = true
      this.hasItemChanges = false
      this.fetchNodes().then(() => {
        this.showSnackBar(SnackBarType.SUCCESS, this.$t('snackbar.success'))
      }, () => {
        this.showSnackBar(SnackBarType.ERROR, this.$t('snackbar.error'))
      }).finally(() => {
        this.pending = false
      })
    },
    showSnackBar (type, message) {
      switch (type) {
        case SnackBarType.SUCCESS:
          this.snackBarTimeout = 3000
          break
        case SnackBarType.INFO:
        case SnackBarType.ERROR:
          this.snackBarTimeout = 9000
          break
      }
      this.snackBarMessage = message
      this.snackbar = true
    },
    formatDistanceToNow,
    openWizard () {
      this.wizardIsOpen = true
    },
    closeWizard () {
      this.wizardIsOpen = false
    }
  },
  components: {
    'app-deploy': DeployComponent,
    'app-issue': IssueComponent,
    'app-lazy-value': LazyValue,
    'app-node-attribute-list': NodeAttributeListComponent
  }
}
</script>

<style lang="scss" scoped>
.search {
  max-width: 25rem;
}

.pending {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  text-align: center;

  > .v-progress-circular {
    top: calc(50% - 35px);
  }
}

.ext-link {
  margin-right: 1.5rem;
  position: relative;

  > .v-icon {
    position: absolute;
    right: -1rem;
    top: 2px;
  }
}
</style>

<i18n>
{
  "en": {
    "title": "List View",
    "network": "Your Unryo Network",
    "search": "Search",
    "add_components": "Add components",
    "refresh_tip": "Trigger nodes synchronization.",
    "refresh_warning_tip": "Some nodes were removed or added.",
    "na": "N/A",
    "columns": {
      "name": "Name",
      "version": "Version",
      "namespace": "Namespace",
      "deployment_status": "Status",
      "availability": "Availability",
      "performance": "Performance",
      "type": "Type",
      "role": "Interconnections Role(s)",
      "protocols": "Communication Protocols",
      "externalIp": "External IP Address",
      "provider": "Provider",
      "latest_report": "Last Seen"
    },
    "groups": {
      "analytic": "Analytics",
      "collector": "Collector",
      "database": "Database",
      "report": "Report",
      "portal": "Portal",
      "vault": "Vault",
      "other": "Other"
    },
    "tips": {
      "name": "Name of the node.",
      "version": "Version of the image the node is running.",
      "namespace": "The environment where the node belongs. It is used to create a network of services, to implement multitenancy among the same owner of applications.",
      "deployment_status": "The node is ONLINE or OFFLINE.",
      "type": "Type of the node.",
      "externalIp": "External IP address of the node.",
      "provider": "The service provider hosting the node.",
      "latest_report": "The last time the node was seen by the Unryo Registry Server.",
      "availability": "Health state based on node availability.",
      "performance": "Health state based on node performance."
    },
    "status": {
      "online": "ONLINE",
      "offline": "OFFLINE"
    },
    "snackbar": {
      "success": "Table has been successfully updated.",
      "info": "Some nodes were removed or added. You can use the reload icon on this table to update it.",
      "error": "An error occured.",
      "close": "Close"
    }
  }
}
</i18n>
